import React, { useEffect, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Award } from "lucide-react";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Reports() {
    const { isAdmin, isApprover } = useAuth();
    const [vendorPerf, setVendorPerf] = useState(null);
    const [teamPerf, setTeamPerf] = useState(null);

    useEffect(() => {
        if (isAdmin() || isApprover()) api.get("/dashboard/vendor-performance").then(r => setVendorPerf(r.data)).catch(() => {});
        if (isAdmin()) api.get("/dashboard/team-performance").then(r => setTeamPerf(r.data)).catch(() => {});
    }, []); // eslint-disable-line

    return (
        <div data-testid="reports-page" className="space-y-8">
            <div>
                <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Analytics</div>
                <h1 className="text-3xl font-heading font-semibold text-stone-900">Reports</h1>
                <p className="text-sm text-stone-500 mt-1">Vendor performance, incentive leaderboard and savings.</p>
            </div>

            {vendorPerf && (
                <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-stone-200 flex items-center gap-2">
                        <Trophy size={16} className="text-amber-600" />
                        <div className="font-heading font-semibold text-stone-900">Vendor performance</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                    <th className="text-left py-3 px-4">Vendor</th>
                                    <th className="text-left py-3 px-4">Status</th>
                                    <th className="text-right py-3 px-4">Submissions</th>
                                    <th className="text-right py-3 px-4">Wins</th>
                                    <th className="text-right py-3 px-4">Win rate</th>
                                    <th className="text-right py-3 px-4">Delivery</th>
                                    <th className="text-right py-3 px-4">Quality</th>
                                    <th className="text-right py-3 px-4">Reliability</th>
                                    <th className="text-right py-3 px-4">Overall</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vendorPerf.vendors.map(v => (
                                    <tr key={v.vendor_id} className="border-t border-stone-100">
                                        <td className="py-3 px-4 font-medium">{v.vendor_name}</td>
                                        <td className="py-3 px-4 text-xs capitalize">{v.status}</td>
                                        <td className="py-3 px-4 text-right">{v.submissions}</td>
                                        <td className="py-3 px-4 text-right">{v.wins}</td>
                                        <td className="py-3 px-4 text-right">{v.win_rate.toFixed(1)}%</td>
                                        <td className="py-3 px-4 text-right">{v.avg_delivery || "—"}</td>
                                        <td className="py-3 px-4 text-right">{v.avg_quality || "—"}</td>
                                        <td className="py-3 px-4 text-right">{v.avg_reliability || "—"}</td>
                                        <td className="py-3 px-4 text-right font-semibold text-amber-700">{v.avg_overall || "—"}</td>
                                    </tr>
                                ))}
                                {vendorPerf.vendors.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-stone-500 text-sm">No vendor data yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {teamPerf && (
                <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-stone-200 flex items-center gap-2">
                        <Award size={16} className="text-amber-600" />
                        <div className="font-heading font-semibold text-stone-900">Team leaderboard — savings ranking</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                    <th className="text-left py-3 px-4">#</th>
                                    <th className="text-left py-3 px-4">Member</th>
                                    <th className="text-left py-3 px-4">Department</th>
                                    <th className="text-right py-3 px-4">Projects</th>
                                    <th className="text-right py-3 px-4">Total saving (₹)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teamPerf.members.map((m, i) => (
                                    <tr key={m.user_id} className="border-t border-stone-100">
                                        <td className="py-3 px-4 font-mono text-stone-500">{i + 1}</td>
                                        <td className="py-3 px-4 font-medium">{m.name}</td>
                                        <td className="py-3 px-4 text-stone-700">{m.department}</td>
                                        <td className="py-3 px-4 text-right">{m.projects_handled}</td>
                                        <td className="py-3 px-4 text-right font-semibold text-green-700">{inr(m.saving)}</td>
                                    </tr>
                                ))}
                                {teamPerf.members.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-stone-500 text-sm">No team activity yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
